import { Component, OnInit, ViewChild } from '@angular/core';
import { AlertController, ModalController, NavParams } from '@ionic/angular';

import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { TagsService } from 'src/services/tags.service';

@Component({
  selector: 'app-edit-tag',
  templateUrl: './edit-tag.component.html',
  styleUrls: ['./edit-tag.component.scss'],
})
export class EditTagComponent implements OnInit {
  @ViewChild('nameInput', { static: false }) nameInput;
  projectName: string;
  projectId: string | null = null;
  showProjectSelector: boolean = false;

  isLoading = false;
  name: string;
  description: string;
  id?: string;
  autoTaggingEnabled = true;
  hasLoadedTag = false;
  hasTagLoadFailed = false;

  constructor(
    navParams: NavParams,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private msgSrvc: MessageService,
    private tagsService: TagsService,
    public authService: AuthService,
    private searchablePopoverService: SearchablePopoverService,
  ) {
    this.id = navParams.get('id');
    this.projectName = 'All projects';

    if (!this.id) {
      const projectId = navParams.get('projectId');

      if (projectId !== null) {
        const recentProject = this.authService.mostRecentProject;

        this.projectId = projectId ?? recentProject?.id ?? this.authService.selectedProject?.id ?? null;
        this.projectName =
          this.authService.projects.find((project) => project.id === this.projectId)?.name ??
          (this.projectId ? 'Selected project' : 'All projects');
      }
    }

    this.showProjectSelector = !this.projectId || this.authService.isTenantAdmin;
  }

  ngOnInit(): void {
    if (this.id) {
      this.loadTag();
    } else {
      setTimeout(() => this.nameInput.setFocus(), 500);
    }
  }

  selectProject(event: any) {
    if (this.id && !this.hasLoadedTag) {
      return;
    }

    this.searchablePopoverService.projects({
      callback: (id, name, project) => {
        this.projectId = id;
        this.projectName = name;
      },
      event,
      selectedProjectId: this.projectId,
      includeTemplates: true,
      showNoneOption: true,
      noneOptionLabel: 'All projects',
    });
  }

  async loadTag() {
    this.isLoading = true;
    this.hasTagLoadFailed = false;

    try {
      const tag = await this.tagsService.getById(this.id);

      this.name = tag.name;
      this.description = tag.description;
      this.autoTaggingEnabled = tag.autoTaggingEnabled;
      this.projectId = tag.projectId;
      this.projectName = tag.project?.name ?? 'All projects';
      this.hasLoadedTag = true;
    } catch (error) {
      this.hasTagLoadFailed = true;

      await this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async submit() {
    if (this.id && this.hasTagLoadFailed) {
      await this.loadTag();

      return;
    }

    if (this.id && !this.hasLoadedTag) {
      await this.msgSrvc.show('Tag details are still loading. Try again in a moment.');

      return;
    }

    if (!this.name) {
      await this.msgSrvc.show('Whoops! Please enter a name.');

      return;
    }

    this.isLoading = true;

    try {
      if (this.id) {
        await this.tagsService.updatePartial(this.id, {
          name: this.name,
          description: this.description,
          autoTaggingEnabled: this.autoTaggingEnabled,
          projectId: this.projectId,
        });

        await this.msgSrvc.show('Tag updated!');
      } else {
        await this.tagsService.create({
          name: this.name,
          description: this.description,
          autoTaggingEnabled: this.autoTaggingEnabled,
          projectId: this.projectId,
        });

        await this.msgSrvc.show('Tag added!');
      }

      this.dismiss();
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async delete() {
    const alert = await this.alertCtrl.create({
      header: 'Are you sure?',
      subHeader: `This can't be undone`,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Delete',
          handler: async () => {
            try {
              this.isLoading = true;
              await this.tagsService.delete(this.id);
              this.isLoading = false;
              this.dismiss();
            } catch (e) {
              this.isLoading = false;
              this.msgSrvc.show(`Unable to delete tag: ${e}`);
            }
          },
        },
      ],
    });
    alert.present();
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }
}
